import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, InputGroup, Pagination } from 'react-bootstrap';
import { Search, Info } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import './Courses.css';

const Courses = () => {
    const { lang } = useLanguage();
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const coursesPerPage = 16;

    const content = {
        en: {
            title: "All Courses",
            searchPlaceholder: "Search...",
            generalDuration: "Standard course duration: ~1 week",
            showing: "Showing",
            results: "results",
            contactText: "Contact for Availability",
            noResults: "No courses found."
        },
        jp: {
            title: "全コース",
            searchPlaceholder: "検索...",
            generalDuration: "標準的な受講期間：約1週間",
            showing: "表示中:",
            results: "件の結果",
            contactText: "空き状況をお問い合わせ",
            noResults: "コースが見つかりませんでした。"
        }
    };

    const t = content[lang];

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                //const response = await fetch('http://localhost:5000/api-portal/courses');
                const response = await fetch('/api-portal/courses');
                if (response.ok) {
                    const data = await response.json();
                    setCourses(data);
                }
            } catch (error) {
                console.error("Error fetching courses:", error);
            }
        };
        fetchCourses();
    }, []);

    const filteredCourses = courses.filter(course => {
        const term = searchTerm.toLowerCase();
        const titleEn = (course.title || "").toLowerCase();
        const titleJp = (course.titleJp || "").toLowerCase();
        return titleEn.includes(term) || titleJp.includes(term);
    });

    const indexOfLastCourse = currentPage * coursesPerPage;
    const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
    const currentCourses = filteredCourses.slice(indexOfFirstCourse, indexOfLastCourse);
    const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

    const handleCourseClick = (course) => {
        navigate(`/course/${course.id}`, { state: { course } });
    };

    return (
        <div className="courses-page-container">
            <Container>
                {/* Header: Title in Center, Search on Right */}
                <div className="courses-header-wrapper mb-5 animate-in">
                    <Row className="align-items-center">
                        <Col xs={4} className="d-none d-lg-block">
                            <p className="results-count m-0 text-red">
                                {t.showing} {filteredCourses.length} {t.results}
                            </p>
                            <p className="text-black small m-0" style={{ letterSpacing: '1px', fontWeight: '500' }}>
                                {t.generalDuration}
                            </p>

                        </Col>
                        <Col xs={12} lg={4} className="text-center">
                            <h1 className="courses-main-title">{t.title}</h1>
                        </Col>
                        <Col xs={12} lg={4} className="mt-3 mt-lg-0">
                            <InputGroup className="search-bar-wrapper shadow-sm">
                                <InputGroup.Text className="search-icon-box">
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder={t.searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="search-input"
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                </div>

                {/* Grid: 4x4 */}
                <Row className="g-4">
                    {currentCourses.length > 0 ? (
                        currentCourses.map(course => (
                            <Col key={course.id} xl={3} lg={4} md={6} sm={6}>
                                <div className="course-card-sharp" onClick={() => handleCourseClick(course)}>
                                    <div className="course-img-frame">
                                        <img
                                            //src={course.image ? `http://localhost:5000/api-portal/uploads/courses/${course.image}` : '/placeholder.jpg'}
                                            src={course.image ? `/api-portal/uploads/courses/${course.image}` : '/placeholder.jpg'}
                                            alt={lang === 'jp' ? course.titleJp : course.title}

                                        />
                                        <div className="course-overlay-sharp">
                                            <span>{t.contactText}</span>
                                        </div>
                                    </div>

                                    <div className="course-info-sharp">
                                        <h3>
                                            {lang === 'jp' ? course.titleJp : course.title}
                                        </h3>
                                    </div>
                                </div>
                            </Col>
                        ))
                    ) : (
                        <Col className="text-center py-5">
                            <Info size={40} className="text-muted mb-2" />
                            <p>{t.noResults}</p>
                        </Col>
                    )}
                </Row>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-container mt-5">
                        <Pagination className="justify-content-center">
                            <Pagination.Prev
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            />
                            {[...Array(totalPages)].map((_, idx) => (
                                <Pagination.Item
                                    key={idx + 1}
                                    active={idx + 1 === currentPage}
                                    onClick={() => setCurrentPage(idx + 1)}
                                >
                                    {idx + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            />
                        </Pagination>
                    </div>
                )}
            </Container>
        </div>
    );
};

export default Courses;